import type {CSSProperties, TransitionEvent} from 'react';
import {twx} from '@/utils/tailwindUtils';
import type {PaneState, usePageTransition} from './usePageTransition';

type PaneProps = {
    pane: PaneState;
    isAnimating: boolean;
    onTransitionEnd: () => void;
};

const Pane = ({pane, isAnimating, onTransitionEnd}: PaneProps) => {
    if (!isAnimating && !pane.outlet) return null;

    const dynamicStyle: CSSProperties = isAnimating
        ? {
              transform: pane.transform,
              transition: pane.transition,
              zIndex: pane.zIndex,
          }
        : {};

    const handleTransitionEnd = (e: TransitionEvent<HTMLDivElement>) => {
        if (
            e.target === e.currentTarget &&
            e.propertyName === 'transform' &&
            isAnimating &&
            pane.shouldCompleteOnTransitionEnd
        ) {
            onTransitionEnd();
        }
    };

    return (
        <div
            className={twx(
                'grid min-h-0 grid-cols-[100%] grid-rows-[1fr]',
                isAnimating ? 'absolute inset-0 overflow-hidden' : 'relative w-full flex-1',
                pane.visible
                    ? twx('pointer-events-auto visible', isAnimating && 'will-change-transform')
                    : 'pointer-events-none invisible will-change-auto'
            )}
            style={dynamicStyle}
            onTransitionEnd={handleTransitionEnd}
        >
            {pane.outlet}
        </div>
    );
};

type PageTransitionProps = ReturnType<typeof usePageTransition>;

export const PageTransition = ({
    foregroundPane,
    backgroundPane,
    isAnimating,
    finishTransition,
}: PageTransitionProps) => {
    return (
        <div className={twx('relative flex h-full w-full flex-1 flex-col', isAnimating && 'overflow-hidden')}>
            <Pane pane={foregroundPane} isAnimating={isAnimating} onTransitionEnd={finishTransition} />
            <Pane pane={backgroundPane} isAnimating={isAnimating} onTransitionEnd={finishTransition} />
        </div>
    );
};
